// Keyboard navigation of the results (combobox pattern): focus stays in the search field, the
// arrow keys move a "virtual" highlight through the entries (aria-activedescendant), Enter opens it.
import { useEffect, useState, type KeyboardEvent } from 'react';
import type { SearchEntry } from './model';

export interface ListNavigation {
  /** DOM id of the highlighted entry, if any (aria-activedescendant). */
  activeId: string | undefined;
  /** Highlights an entry (pointer). */
  setActiveId: (id: string | undefined) => void;
  /** Key handler of the search field. */
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
}

interface Options {
  entries: readonly SearchEntry[];
  onOpen: (entry: SearchEntry) => void;
  /** Enter with no highlighted entry. */
  onSubmit: () => void;
}

/**
 * ArrowDown / ArrowUp walk through the entries across groups (and wrap round), Enter opens the
 * highlighted one, or submits the query when none is highlighted. Home and End jump to the first
 * and last entry once the highlight is in the list; before that they keep moving the caret in the
 * field, as a text field must.
 */
export function useListNavigation({ entries, onOpen, onSubmit }: Options): ListNavigation {
  const [chosen, setChosen] = useState<string | undefined>();
  // A highlight that is no longer in the list (new results) is dropped.
  const activeId = entries.some((entry) => entry.id === chosen) ? chosen : undefined;

  useEffect(() => {
    if (!activeId) return;
    const element = document.getElementById(activeId);
    if (element && typeof element.scrollIntoView === 'function')
      element.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing || event.altKey || event.ctrlKey || event.metaKey) return;
    const count = entries.length;
    const index = entries.findIndex((entry) => entry.id === activeId);
    const go = (next: number) => {
      event.preventDefault();
      setChosen(entries[next]?.id);
    };
    switch (event.key) {
      case 'ArrowDown':
        if (count > 0) go(index < 0 ? 0 : (index + 1) % count);
        break;
      case 'ArrowUp':
        if (count > 0) go(index <= 0 ? count - 1 : index - 1);
        break;
      case 'Home':
        if (index >= 0) go(0);
        break;
      case 'End':
        if (index >= 0) go(count - 1);
        break;
      case 'Enter': {
        event.preventDefault();
        const entry = entries[index];
        if (entry) onOpen(entry);
        else onSubmit();
        break;
      }
      default:
    }
  };

  return { activeId, setActiveId: setChosen, onKeyDown };
}
